import type { GuidelineRepresentation } from '@atlassian/data-classification-level';

import type { ClassificationLevel } from '../ClassificationRadioOption.tsx';

type GuidelineProps = {
	guideline: string | undefined;
	guidelineRepresentation: GuidelineRepresentation;
};

/**
 * Returns the appropriate guideline value and representation format.
 *
 * When ADF guideline data exists, this function returns the ADF content with 'EDITOR'
 * representation. Otherwise, it falls back to the plain text guideline with 'PLAIN' representation.
 *
 * @param classification - The classification level containing guideline fields
 * @returns Object containing:
 *   - guideline: The guideline content (ADF string or plain text, or undefined if neither exists)
 *   - guidelineRepresentation: 'EDITOR' for ADF content, 'PLAIN' for plain text
 *
 * @example
 * // With guidelineADF present
 * const { guideline, guidelineRepresentation } = getGuidelineProps(classification);
 * // Returns: { guideline: '{\"type\":\"doc\"...}', guidelineRepresentation: 'EDITOR' }
 *
 * @example
 * // With no guidelineADF
 * const { guideline, guidelineRepresentation } = getGuidelineProps(classification);
 * // Returns: { guideline: 'Plain text guideline', guidelineRepresentation: 'PLAIN' }
 */
export const getGuidelineProps = (
	classification: ClassificationLevel | null | undefined,
): GuidelineProps => {
	// Prefer ADF guideline over plain text
	if (classification?.guidelineADF) {
		return {
			guideline: classification.guidelineADF,
			guidelineRepresentation: 'EDITOR',
		};
	}

	// Fall back to plain text guideline
	return {
		guideline: classification?.guideline,
		guidelineRepresentation: 'PLAIN',
	};
};
